import { RentalAgreement } from "@/apps/tatar/cashBudget/views/tenants/TenantsInterfaces";
import { OAObject } from "@/apps/tatar/objectsApp/types/object.interface";
import i18n from "@/i18n";
import { AssetTypes } from "@/model/AssetTypes";
import { LETTER_HEADER_VARIABLE } from "@/modules/abstract-ui/forms/document-editor/BFDocument.service";
import { VariableItem } from "@/modules/ckeditor/TextEditor";
import OrgaStruct from "@/redux/actions/struct/implemented/OrgaStruct";
import CacheService from "@/services/CacheService";
import ArrayUtils from "@/utils/ArrayUtils";
import StringUtils from "@/utils/StringUtils";
import { getRentalPlanVariable } from "./variable-content/TenantCommunicationRentalPlanVariable";

const COMMUNICATION_VAROIABLES: (search?: string) => VariableItem[] = (
  search?: string
) => {
  const variables = [
    ...LETTER_HEADER_VARIABLE(),
    {
      id: "salutation",
      name: i18n.t("apTenantCommunication.variables.salutation", "Anrede"),
    },
    {
      id: "tenantId",
      name: i18n.t("apTenantCommunication.variables.tenantId", "Mieternummer"),
    },
  ];
  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;
  variables.push({
    id: `rentalPlan:currentYear`,
    preventEdit: true,
    name: i18n.t(
      "apTenantCommunication.variables.rentalPlan",
      "Mietzinstabelle {{year}}",
      {
        year: currentYear,
      }
    ),
  });
  variables.push({
    id: `rentalPlan:nextYear`,
    preventEdit: true,
    name: i18n.t(
      "apTenantCommunication.variables.rentalPlan",
      "Mietzinstabelle {{year}}",
      {
        year: nextYear,
      }
    ),
  });
  return variables;
};
export const getTenantCommunicationVariables = (search: string) => {
  if (!search) {
    return COMMUNICATION_VAROIABLES();
  }
  return ArrayUtils.fuzzyFilter(search, COMMUNICATION_VAROIABLES(search), {
    keys: ["name"],
  }).map((e) => e.item);
};

export const getTenantCommunicationVariablesFor = async (
  rentalAgreement: RentalAgreement,
  addressId?: string,
  ignoreComplexVariables?: boolean
) => {
  const usedAddress =
    rentalAgreement.expand.tenant?.data.address?.find(
      (e) => e.id === addressId
    ) || rentalAgreement.expand.tenant?.data.address?.find((e) => e.isMain);

  const entity = OrgaStruct.getEntity(rentalAgreement.data.entity);

  const object = OrgaStruct.getObject(rentalAgreement.data.objectId);

  const portfolioObject = (await CacheService.getData({
    oType: "asset",
    assetType: AssetTypes.Portfolio.Object,
    id: rentalAgreement.data.objectId,
  })) as OAObject;
  const objectAddress = portfolioObject.data.feature.address?.address?.find(
    (e) => e.isMain
  );
  const objectCity = objectAddress?.city;
  const objectZip = objectAddress?.postalcode;
  const objectStreetName = objectAddress?.streetname;
  const objectStreetNumber = objectAddress?.streetnumber;
  const bankAccountId = object.bankInfo.mainBankAccount;
  const bankAccount = OrgaStruct.getBankAccount(bankAccountId);
  const iban = StringUtils.formatIban(bankAccount?.iban);
  const bic = bankAccount?.blz;

  const units = await CacheService.getDataMultiple(
    rentalAgreement.data.rentalUnits.map((rentalUnitId) => ({
      assetType: AssetTypes.Rental.RentalUnit,
      assetId: rentalUnitId,
    }))
  );

  const unitsName = units?.map((unit) => unit.data.displayName).join(", ");

  const bankName = bankAccount?.bankName;
  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;
  //todo create html stuff for variables

  const area = _.sum(units?.map((unit) => unit.data.area || 0));
  const totalRent = rentalAgreement.data.rentGrossTotal || 0;
  const rentNet = rentalAgreement.data.rentNet || 0;
  const rentGross = rentalAgreement.data.rentGross || 0;

  const operatingCostNet = rentalAgreement.data.operatingCostNet || 0;
  const operatingCostGross = rentalAgreement.data.operatingCostGross || 0;
  const rentNetPerQM = rentNet / area;
  const rentGrossPerQM = rentGross / area;
  const operatingCostGrossPerQM = operatingCostGross / area;
  const operatingCostNetPerQM = operatingCostNet / area;
  const totalRentPerQM = totalRent / area;

  const output = {
    iban: iban || "",
    bic: bic || "",
    objectName: object.displayName,
    objectIdentifier: object.id,
    bankName: bankName || "",
    documentDate: StringUtils.formatDate(new Date()),
    receiverName: rentalAgreement.expand.tenant?.data.displayName || "",
    receiverStreet:
      [usedAddress.street, usedAddress.additional].join(" ") || "",
    receiverCity: [usedAddress.zip, usedAddress.city].join(" ") || "",
    receiverCountry: usedAddress.country?.toUpperCase() || "",
    tenantId: rentalAgreement.data.id,
    senderName: entity?.displayName,
    units: unitsName || "",

    rentNet: StringUtils.formatCurrency(rentNet) || "",
    rentGross: StringUtils.formatCurrency(rentGross) || "",
    totalRent: StringUtils.formatCurrency(totalRent) || "",
    rentNetPerQM: StringUtils.formatCurrency(rentNetPerQM) || "",
    rentGrossPerQM: StringUtils.formatCurrency(rentGrossPerQM) || "",
    totalRentPerQM: StringUtils.formatCurrency(totalRentPerQM) || "",
    area: StringUtils.formatArea(area) || "",
    operatingCostNet: StringUtils.formatCurrency(operatingCostNet) || "",
    operatingCostGross: StringUtils.formatCurrency(operatingCostGross) || "",
    operatingCostGrossPerQM:
      StringUtils.formatCurrency(operatingCostGrossPerQM) || "",
    operatingCostNetPerQM:
      StringUtils.formatCurrency(operatingCostNetPerQM) || "",

    objectCity: objectCity || "",
    objectZip: objectZip || "",
    objectStreetName: objectStreetName || "",
    objectStreetNumber: objectStreetNumber || "",
    senderStreet: entity?.address?.street || "",
    senderZip: entity?.address?.zip || "",
    senderCity: entity?.address?.city || "",
    senderCountry: entity?.address?.country || "",
    senderPhone: "",
    senderFax: "",
    senderMail: "",
    senderWebsite: "",
    salutation: rentalAgreement.expand.tenant.data.salutationText,
  };

  if (!ignoreComplexVariables) {
    output["rentalPlan:currentYear"] = await getRentalPlanVariable(
      rentalAgreement,
      currentYear
    );
    output["rentalPlan:nextYear"] = await getRentalPlanVariable(
      rentalAgreement,
      nextYear
    );
  }

  return output;
};
